/**
 * @fileoverview Flagged Transactions — review workspace (MOR-514)
 * @module jobs/phases/review/flaggedTransactions/FlaggedTransactionsWorkspace
 *
 * Layout per the ticket: master agent chat on the left, content on the
 * right — the master review table (one row per managed analysis agent) by
 * default, a focused single-agent view when opened, and an all-flags view
 * across every agent. Case-level summary + case lock + insolvency-date
 * override sit above the table.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import {
  ArrowLeft,
  CalendarClock,
  Loader2,
  Lock,
  LockOpen,
  MessageSquare,
  Pencil,
  Play,
  RefreshCw,
  Rows3,
  Table2,
} from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import { BankruptcyCaseChips } from "./BankruptcyCaseChips";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { MasterChatSheet } from "./MasterChatSheet";
import { MasterReviewTable } from "./MasterReviewTable";
import { VoidableTransactionsExportButton } from "./VoidableTransactionsExportButton";
import { FocusedHeadView } from "./FocusedHeadView";
import { TransactionTable } from "./TransactionTable";
import {
  agentText,
  fmtDate,
  fmtMoney,
  INSOLVENCY_BASIS_LABEL,
  type FlaggedHeadKey,
  type WorkspaceUI,
} from "./flaggedShared";

type View =
  | { kind: "master" }
  | { kind: "head"; headKey: FlaggedHeadKey }
  | { kind: "all" };

export function FlaggedTransactionsWorkspace(props: {
  jobId: Id<"jobs">;
  isViewer: boolean;
  onBack: () => void;
  /**
   * `?head=` deep link. An unknown key falls through to the master table,
   * because the focused view only renders once the key resolves against the
   * loaded workspace.
   */
  initialHead?: string;
}) {
  const { jobId, isViewer, onBack, initialHead } = props;
  // Persist the chosen table (master vs all-flags) per job so reopening the
  // workspace restores the last view. localStorage = per-user/per-browser.
  // The focused "head" view is transient and never persisted.
  const viewKey = `flagged:view:${jobId}`;
  const [view, setView] = useState<View>(() => {
    // Seeded rather than applied in an effect so a deep link doesn't flash
    // the master table first.
    if (initialHead) {
      return { kind: "head", headKey: initialHead as FlaggedHeadKey };
    }
    if (
      typeof window !== "undefined" &&
      window.localStorage.getItem(viewKey) === "all"
    ) {
      return { kind: "all" };
    }
    return { kind: "master" };
  });
  // Catches a ?head= change while this workspace is already mounted.
  useEffect(() => {
    if (initialHead) {
      setView({ kind: "head", headKey: initialHead as FlaggedHeadKey });
    }
  }, [initialHead]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (view.kind === "master" || view.kind === "all") {
      window.localStorage.setItem(viewKey, view.kind);
    }
  }, [view, viewKey]);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const { data } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.flaggedTransactions.publicQueries
        .getWorkspace,
      { jobId },
    ),
  );
  const workspace = data as WorkspaceUI | undefined;

  const runHeads = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .runHeads,
    ),
  });
  const setCaseLock = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .setCaseLock,
    ),
  });
  const setHeadLock = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .setHeadLock,
    ),
  });

  // Lazily supersedes the pre-redesign per-employee flags on the
  // defeat-entitlements head, so existing jobs converge on load without a
  // run-once migration. Idempotent; a clean job is a no-op.
  const retireLegacyEmployeeFlags = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .retireLegacyEmployeeFlags,
    ),
  });
  const retireLegacy = retireLegacyEmployeeFlags.mutate;
  // Lazily supersedes flags and removes head rows for heads outside the
  // job's regime (MOR-1254): a bankruptcy job that ran the corporate heads
  // before gating existed converges on load, with no run-once migration.
  const retireWrongRegimeRows = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .retireWrongRegimeRows,
    ),
  });
  const retireWrongRegime = retireWrongRegimeRows.mutate;
  // Lazily supersedes flags whose register entry was merged or removed since
  // the last run (QA R-01), so consolidated registers stop inflating the
  // case total the moment the workspace opens.
  const retireStaleRegisterFlags = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .retireStaleRegisterFlags,
    ),
  });
  const retireStale = retireStaleRegisterFlags.mutate;
  useEffect(() => {
    retireLegacy({ jobId });
    retireWrongRegime({ jobId });
    retireStale({ jobId });
  }, [retireLegacy, retireWrongRegime, retireStale, jobId]);

  const focusedHead = useMemo(() => {
    if (!workspace || view.kind !== "head") return null;
    return workspace.heads.find((h) => h.headKey === view.headKey) ?? null;
  }, [workspace, view]);

  if (!workspace) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const anyUpdating = workspace.heads.some((h) => h.status === "updating");

  const runAll = async () => {
    try {
      const result = (await runHeads.mutateAsync({ jobId })) as {
        scheduled: number;
        lockedSkips: number;
      };
      toast.success(
        `${result.scheduled} agents scheduled${result.lockedSkips > 0 ? ` (${result.lockedSkips} locked — marked stale)` : ""}`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to schedule");
    }
  };

  const toggleCaseLock = async () => {
    try {
      await setCaseLock.mutateAsync({
        jobId,
        locked: !workspace.caseLocked,
      });
      toast.success(
        workspace.caseLocked
          ? "Case review unlocked"
          : "Case review locked — agents now propose instead of update",
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    }
  };

  // Per-agent actions for the focused-head view, surfaced in the top bar.
  const rerunHead = async (headKey: FlaggedHeadKey) => {
    try {
      const result = (await runHeads.mutateAsync({
        jobId,
        headKeys: [headKey],
      })) as { scheduled: number; lockedSkips: number };
      if (result.lockedSkips > 0) {
        toast.info("Agent is locked — marked stale instead of running");
      } else {
        toast.success("Agent re-running now");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to schedule");
    }
  };

  const toggleHeadLock = async (headKey: FlaggedHeadKey, locked: boolean) => {
    try {
      await setHeadLock.mutateAsync({ jobId, headKey, locked: !locked });
      toast.success(locked ? "Agent unlocked" : "Agent output locked");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    }
  };

  return (
    <div className="flex min-h-full flex-1 flex-col p-6">
      <div className="-m-6 flex flex-1 flex-col gap-4 bg-muted px-6 pb-6 pt-6">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {view.kind === "head" ? (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setView({ kind: "master" })}
              >
                <ArrowLeft className="h-4 w-4" />
                Back to table
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={onBack}
              >
                <ArrowLeft className="h-4 w-4" />
                Investigations
              </Button>
            )}
            <div>
              <h1 className="text-base font-semibold leading-none">
                Recoverable Transactions
              </h1>
              <p className="mt-1 text-xs text-muted-foreground">
                {workspace.totals.flagCount} transactions across{" "}
                {workspace.totals.headsWithFlags} claims ·{" "}
                {fmtMoney(workspace.totals.dedupedRecoverable)} estimated
                recoveries
              </p>
            </div>
            {workspace.caseLocked && (
              <Badge color="violet" variant="soft" size="sm">
                <Lock className="mr-1 h-3 w-3" />
                Case locked
              </Badge>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {/* Bankruptcy anchors (MOR-1260): commencement, date of
                bankruptcy and petition type lead on a personal job; the
                insolvency date stays because s 122 still needs it. */}
            {view.kind !== "head" &&
              workspace.regime === "personal" &&
              workspace.bankruptcy && (
                <BankruptcyCaseChips bankruptcy={workspace.bankruptcy} />
              )}
            {/* Insolvency date chip — relocated into the agent's stat row when
                a head is focused (see FocusedHeadView). */}
            {view.kind !== "head" && (
              <button
                type="button"
                className="flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1.5 text-xs hover:bg-muted/60"
                onClick={() => !isViewer && setDateDialogOpen(true)}
                title={agentText(workspace.insolvency.note)}
              >
                <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">
                  {workspace.regime === "personal"
                    ? "Est insolvency date:"
                    : "Est Insolvency Date:"}
                </span>
                <span className="font-medium">
                  {workspace.insolvency.date
                    ? fmtDate(workspace.insolvency.date)
                    : "unavailable"}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  (
                  {INSOLVENCY_BASIS_LABEL[workspace.insolvency.basis] ??
                    workspace.insolvency.basis.replace(/_/g, " ")}
                  )
                </span>
                {!isViewer && (
                  <Pencil className="h-3 w-3 text-muted-foreground" />
                )}
              </button>
            )}

            <VoidableTransactionsExportButton
              jobId={jobId}
              workspace={workspace}
            />
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setChatOpen(true)}
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Chat
              {workspace.master.running && (
                <Loader2 className="h-3 w-3 animate-spin" />
              )}
            </Button>
            {view.kind !== "head" && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() =>
                  setView(
                    view.kind === "all" ? { kind: "master" } : { kind: "all" },
                  )
                }
              >
                {view.kind === "all" ? (
                  <>
                    <Table2 className="h-3.5 w-3.5" /> Master table
                  </>
                ) : (
                  <>
                    <Rows3 className="h-3.5 w-3.5" /> All transactions
                  </>
                )}
              </Button>
            )}
            {!isViewer && (
              <>
                {view.kind !== "head" && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      disabled={runHeads.isPending || anyUpdating}
                      onClick={() => void runAll()}
                    >
                      {anyUpdating ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Play className="h-3.5 w-3.5" />
                      )}
                      Run all agents
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      disabled={setCaseLock.isPending}
                      onClick={() => void toggleCaseLock()}
                    >
                      {workspace.caseLocked ? (
                        <LockOpen className="h-3.5 w-3.5" />
                      ) : (
                        <Lock className="h-3.5 w-3.5" />
                      )}
                      {workspace.caseLocked ? "Unlock case" : "Lock case"}
                    </Button>
                  </>
                )}
                {view.kind === "head" && focusedHead && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      disabled={
                        runHeads.isPending || focusedHead.status === "updating"
                      }
                      onClick={() => void rerunHead(focusedHead.headKey)}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Re-run
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      disabled={workspace.caseLocked || setHeadLock.isPending}
                      title={
                        workspace.caseLocked
                          ? "The whole case is locked"
                          : undefined
                      }
                      onClick={() =>
                        void toggleHeadLock(
                          focusedHead.headKey,
                          focusedHead.locked,
                        )
                      }
                    >
                      {focusedHead.locked ? (
                        <LockOpen className="h-3.5 w-3.5" />
                      ) : (
                        <Lock className="h-3.5 w-3.5" />
                      )}
                      {focusedHead.locked ? "Unlock Claim" : "Lock Claim"}
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="min-w-0 flex-1">
          {view.kind === "head" && focusedHead ? (
            <FocusedHeadView
              jobId={jobId}
              head={focusedHead}
              isViewer={isViewer}
              insolvency={workspace.insolvency}
              onEditInsolvencyDate={() => setDateDialogOpen(true)}
              onNavigateToHead={(headKey) => setView({ kind: "head", headKey })}
            />
          ) : view.kind === "all" ? (
            <TransactionTable
              jobId={jobId}
              isViewer={isViewer}
              onNavigateToHead={(headKey) => setView({ kind: "head", headKey })}
            />
          ) : (
            <MasterReviewTable
              heads={workspace.heads}
              onOpenHead={(headKey) => setView({ kind: "head", headKey })}
            />
          )}
        </div>
      </div>

      <MasterChatSheet
        open={chatOpen}
        onOpenChange={setChatOpen}
        jobId={jobId}
        agentBusy={workspace.master.running}
        canSend={!isViewer}
      />

      {dateDialogOpen && (
        <InsolvencyDateDialog
          jobId={jobId}
          current={workspace.insolvency}
          onClose={() => setDateDialogOpen(false)}
        />
      )}
    </div>
  );
}

function InsolvencyDateDialog(props: {
  jobId: Id<"jobs">;
  current: WorkspaceUI["insolvency"];
  onClose: () => void;
}) {
  const { jobId, current, onClose } = props;
  const [date, setDate] = useState(
    current.override?.date ?? current.date ?? "",
  );
  const [reason, setReason] = useState(current.override?.reason ?? "");

  const setOverride = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .setInsolvencyDateOverride,
    ),
  });

  const save = async (clear: boolean) => {
    if (!clear && (!date || reason.trim().length < 3)) {
      toast.error("A date and a short reason are required");
      return;
    }
    try {
      await setOverride.mutateAsync({
        jobId,
        date: clear ? null : date,
        reason: clear
          ? "Override cleared — reverting to the derived date."
          : reason.trim(),
      });
      toast.success(
        clear
          ? "Override cleared — date-sensitive agents re-scheduled"
          : "Insolvency date set — date-sensitive agents re-scheduled",
      );
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Estimated date of insolvency</DialogTitle>
          <DialogDescription className="text-xs">
            {agentText(current.note)}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">Override date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">Reason (audited)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. Settled with the committee on the cash-flow analysis as at…"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          {current.override && (
            <Button
              variant="outline"
              size="sm"
              disabled={setOverride.isPending}
              onClick={() => void save(true)}
            >
              Clear override
            </Button>
          )}
          <Button
            size="sm"
            disabled={setOverride.isPending}
            onClick={() => void save(false)}
          >
            {setOverride.isPending && (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            )}
            Set override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
